import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { ReasoningTunnel } from '@/tunnels/reasoning';
import { PlayIcon } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';

interface AgentResultProps {
  messages: Array<{
    role: string;
    content: string;
    parts?: Array<{
      type: string;
      details?: Array<{
        type: string;
        text?: string;
        data?: string;
        signature?: string;
      }>;
    }>;
  }>;
  status: string;
  generatedResult?: string;
  onScrollToBottom?: () => void;
}

// Utility function to remove XML tags and keep only the content
function stripXmlTags(content: string): string {
  return content.replace(/<[^>]*>/g, '');
}

export function AgentResult({
  messages,
  status,
  generatedResult,
  onScrollToBottom,
}: AgentResultProps) {
  const responseAreaRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom function
  const scrollToBottom = useCallback(() => {
    if (responseAreaRef.current) {
      responseAreaRef.current.scrollTop = responseAreaRef.current.scrollHeight;
    }
    onScrollToBottom?.();
  }, [onScrollToBottom]);

  // Auto-scroll to bottom when streaming
  useEffect(() => {
    if (status === 'streaming') {
      scrollToBottom();
    }
  }, [status, scrollToBottom]);

  // Listener for streaming status to keep content at bottom
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (status === 'streaming') {
      // Aggressively scroll to bottom every 50ms while streaming
      intervalId = setInterval(() => {
        if (responseAreaRef.current) {
          responseAreaRef.current.scrollTop =
            responseAreaRef.current.scrollHeight;
        }
      }, 50);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [status]);

  const nonUserMessages = messages.filter((message) => message.role !== 'user');

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (nonUserMessages.length > 0) {
      scrollToBottom();
    }
  }, [nonUserMessages.length, scrollToBottom]);

  return (
    <>
      {/* Response Area */}
      <div className="space-y-3 p-3">
        <Label className="text-foreground text-xs">Result</Label>
        <div className="relative h-[20rem]">
          <div
            ref={responseAreaRef}
            className={cn(
              'nowheel h-full w-full overflow-auto rounded-t-2xl bg-secondary p-4',
              status === 'streaming' ? 'rounded-b-none pb-16' : 'rounded-b-2xl'
            )}
          >
            {status === 'submitted' && (
              <div className="flex h-full items-center justify-center gap-2 text-emerald-600 text-sm">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
                Starting agent...
              </div>
            )}

            {generatedResult &&
              !nonUserMessages.length &&
              status !== 'submitted' && (
                <ReactMarkdown>{stripXmlTags(generatedResult)}</ReactMarkdown>
              )}

            {!generatedResult &&
              !nonUserMessages.length &&
              status !== 'submitted' && (
                <div className="flex aspect-video h-full w-full items-center justify-center bg-secondary">
                  <p className="text-muted-foreground text-sm">
                    Press{' '}
                    <PlayIcon size={12} className="-translate-y-px inline" /> to
                    run agent
                  </p>
                </div>
              )}

            {Boolean(nonUserMessages.length) &&
              status !== 'submitted' &&
              nonUserMessages.map((message, index) => (
                <ReactMarkdown key={index}>
                  {stripXmlTags(message.content)}
                </ReactMarkdown>
              ))}
          </div>
        </div>
      </div>

      {/* Reasoning Tunnel */}
      <ReasoningTunnel.In>
        {messages.flatMap(
          (message) =>
            message.parts
              ?.filter((part) => part.type === 'reasoning')
              .flatMap((part) =>
                part.details
                  ?.filter((detail) => detail.type === 'text' && detail.text)
                  .map((detail) => stripXmlTags(detail.text as string))
              ) || []
        )}
      </ReasoningTunnel.In>
    </>
  );
}
